import _ from 'lodash';
import Moment from 'moment';
import { matchSorter } from 'match-sorter';
import { styled } from '@mui/material';
import { isStepEditable, sortSubjectivityKeys } from '@cowbell/broker/bind';
import { GENERIC_SUBJECTIVITIES, bindSteps } from '../constants/constants';
import { ProductTypes } from '../../../../types';
import { PaperSnackbar } from '../../../../components/snackbars';
import { formatCurrencyToTenthsPlaceWithUnit } from '../../../../utils/currency.utils';
import { cowbellBindSteps } from '../../cowbell-bind-quote/constants/constants';
import { PERSONA_TYPES } from '../../../_statics/persona.statics';
import { toUniversalUtcDate } from '../../../../utils/date.utils';
import { moneyFormat2Decimals } from '../../../../utils/appUtils';
import {
  deriveExcessAttachment,
  type TowerValue,
} from '../../../../components/tower';
import { REDACTED_FIELD } from '../../../../config/logrocket';

export const overhaulQuoteProgressData = (
  data: any,
  persona: any,
  quoteDetails: any,
  productType: any
) => {
  const isCowbell = persona == PERSONA_TYPES.COWBELL;

  const isUKProduct = determineIsUKProduct(productType);
  const isAUProduct = determineAUProduct(productType);
  const shouldShowCommissionStep = isUKProduct || isAUProduct;

  const { preBindSubjectivities = [], preIssuanceSubjectivities = [] } = data;
  const rawSubjectivities = [
    ...preBindSubjectivities,
    ...preIssuanceSubjectivities,
  ];

  const {
    preBindSteps,
    preBindSubjectivitiesObject,
    preBindStepsCompleted,
    preBindKeys,
  } = getPreBindInfo(preBindSubjectivities);

  const sortedPreBindSteps = sortSubjectivities(preBindSteps);
  const sortedPreBindKeys = sortSubjectivityKeys(preBindKeys);

  const preIssueSteps = preIssuanceSubjectivities.map(
    ({ subjectivityTitle, subjectivityKey }: any) => ({
      subjectivityTitle,
      subjectivityKey,
    })
  );

  const preIssueKeys = preIssuanceSubjectivities.map(
    ({ subjectivityKey }: any) => subjectivityKey
  );

  const sortedPreIssueSteps = sortSubjectivities(preIssueSteps);
  const sortedPreIssueKeys = sortSubjectivityKeys(preIssueKeys);

  const subjectivities = preIssuanceSubjectivities.reduce(
    (acc: any, subjectivity: any) => {
      return {
        ...acc,
        [subjectivity.subjectivityKey]: {
          ...subjectivity,
          // TODO: Temporary. Remove once BE supplies subj types
          ...(GENERIC_SUBJECTIVITIES[subjectivity.subjectivityKey] || {}),
        },
      };
    },
    { ...preBindSubjectivitiesObject }
  );

  const allCowbellPreIssueSteps = [
    { subjectivityTitle: cowbellBindSteps.UPLOAD_BIND_ORDER },
    { subjectivityTitle: bindSteps.POLICY_EFFECTIVE_DATE },
    ...sortedPreBindSteps,
    { subjectivityTitle: bindSteps.BIND_QUOTE },
    ...sortedPreIssueSteps,
  ];

  const allCowbellSteps = [
    ...allCowbellPreIssueSteps,
    { subjectivityTitle: bindSteps.ISSUE_POLICY },
  ];

  const updateCommissionStep = shouldShowCommissionStep
    ? [{ subjectivityTitle: bindSteps.UPDATE_COMMISSION }]
    : [];

  const allAgencyPreIssueSteps = [
    { subjectivityTitle: bindSteps.POLICY_EFFECTIVE_DATE },
    ...updateCommissionStep,
    ...sortedPreBindSteps,
    { subjectivityTitle: bindSteps.BIND_QUOTE },
    ...sortedPreIssueSteps,
  ];

  const allAgencySteps = [
    ...allAgencyPreIssueSteps,
    { subjectivityTitle: bindSteps.ISSUE_POLICY },
  ];

  const agencyInitialSteps = [
    bindSteps.POLICY_EFFECTIVE_DATE,
    ...(shouldShowCommissionStep ? [bindSteps.UPDATE_COMMISSION] : []),
    ...sortedPreBindKeys,
    bindSteps.BIND_QUOTE,
    ...sortedPreIssueKeys,
    bindSteps.ISSUE_POLICY,
  ];

  const cowbellInitialSteps = [
    cowbellBindSteps.UPLOAD_BIND_ORDER,
    bindSteps.POLICY_EFFECTIVE_DATE,
    ...sortedPreBindKeys,
    bindSteps.BIND_QUOTE,
    ...sortedPreIssueKeys,
    bindSteps.ISSUE_POLICY,
  ];

  const initialSteps = isCowbell ? cowbellInitialSteps : agencyInitialSteps;
  const allSteps = isCowbell ? allCowbellSteps : allAgencySteps;
  const allPreIssueSteps = isCowbell
    ? allCowbellPreIssueSteps
    : allAgencyPreIssueSteps;

  const preIssueStepsCompleted = preIssuanceSubjectivities.reduce(
    (acc: any, subjectivity: any) => {
      return {
        ...acc,
        [subjectivity.subjectivityKey]: subjectivity.completed,
      };
    },
    {}
  );

  const stepsCompleted = {
    [cowbellBindSteps.UPLOAD_BIND_ORDER]: !_.isEmpty(
      quoteDetails.uiData.bindOrderDocumentInfo
    ),
    [bindSteps.POLICY_EFFECTIVE_DATE]: true,
    ...(shouldShowCommissionStep
      ? { [bindSteps.UPDATE_COMMISSION]: true }
      : {}),
    ...preBindStepsCompleted,
    [bindSteps.BIND_QUOTE]:
      data.quoteAgencyStatus === 'BOUND' || data.quoteAgencyStatus === 'SIGNED',
    ...preIssueStepsCompleted,
    [bindSteps.ISSUE_POLICY]: data.quoteAgencyStatus === 'SIGNED',
  };

  const incompleteSteps = allSteps.filter((step) => {
    return (
      stepsCompleted[step.subjectivityTitle] === false ||
      stepsCompleted[step.subjectivityKey] === false
    );
  });

  const incompletePreBindSteps = preBindSubjectivities.filter((step: any) => {
    return (
      stepsCompleted[step.subjectivityTitle] === false ||
      stepsCompleted[step.subjectivityKey] === false
    );
  });

  const incompletePreIssueSteps = allPreIssueSteps.filter((step) => {
    return (
      stepsCompleted[step.subjectivityTitle] === false ||
      stepsCompleted[step.subjectivityKey] === false
    );
  });

  const manuscriptSubjectivities = rawSubjectivities.filter((subjectivity) => {
    return subjectivity.subjectivityKey.includes('M');
  });

  return {
    initialSteps,
    sortedPreBindSteps,
    preBindKeys,
    preIssueKeys,
    sortedPreIssueSteps,
    subjectivities,
    incompleteSteps,
    incompletePreBindSteps,
    incompletePreIssueSteps,
    manuscriptSubjectivities,
  };
};

const getPreBindInfo = (preBindSubjectivities: any) => {
  const preBindSubjectivitiesObject = {};
  const preBindStepsCompleted = {};
  const preBindSteps = [];
  const preBindKeys = [];

  for (const subjectivity of preBindSubjectivities) {
    const { subjectivityTitle, subjectivityKey } = subjectivity;
    preBindSubjectivitiesObject[subjectivityKey] = {
      ...subjectivity,
      // TODO: Temporary. Remove once BE supplies subj types
      ...(GENERIC_SUBJECTIVITIES[subjectivityKey] || {}),
    };
    preBindStepsCompleted[subjectivityKey] = subjectivity.completed;
    preBindSteps.push({ subjectivityTitle, subjectivityKey });
    preBindKeys.push(subjectivityKey);
  }
  return {
    preBindSteps,
    preBindKeys,
    preBindSubjectivitiesObject,
    preBindStepsCompleted,
  };
};

const keyOf = ({ subjectivityKey }: { subjectivityKey: string }) =>
  subjectivityKey;

// Ranks each object by where its key lands in the library's key sort.
const sortSubjectivities = (subjectivities: any) => {
  const rank = new Map<string, number>(
    sortSubjectivityKeys(subjectivities.map(keyOf)).map((key, index) => [
      key,
      index,
    ])
  );
  return [...subjectivities].sort(
    (a, b) => rank.get(keyOf(a)) - rank.get(keyOf(b))
  );
};

export const formatUWSubjectivityPayload = (
  availableSubjectivities: any,
  formValues: any
) => {
  if (!_.isArray(availableSubjectivities)) {
    return formValues;
  }

  const subjectivitySettings: any = [];
  availableSubjectivities.map((subjectivity) => {
    let eachSubjectivityValues = {};

    _.entries(formValues).map((formSubjectivity) => {
      const splitKeySubjectivity = formSubjectivity[0].split('_');

      if (subjectivity.id === splitKeySubjectivity[0]) {
        eachSubjectivityValues = {
          ...eachSubjectivityValues,
          [splitKeySubjectivity[1]]: _.isUndefined(formSubjectivity[1])
            ? false
            : formSubjectivity[1],
        };
      }
      return eachSubjectivityValues;
    });

    subjectivitySettings.push({
      subjectivityId: subjectivity.id,
      ...eachSubjectivityValues,
    });
    return subjectivitySettings;
  });

  return subjectivitySettings;
};

export const checkSubjectivityOnOneQuoteFromAll = (
  formField: any,
  formFieldValue: any
) => {
  const splitKeySubjectivity = formField.split('_');
  if (splitKeySubjectivity[1] === 'addToAllQuotes') {
    return {
      name: `${splitKeySubjectivity[0]}_addToThisQuote`,
      value: formFieldValue,
    };
  }
  return null;
};

export const formatSubjectivities = (availableSubjectivities: any) => {
  if (
    !_.isArray(availableSubjectivities) ||
    availableSubjectivities.length === 0
  ) {
    return [];
  }

  const preBindSubjectivities = [];
  const preIssuanceSubjectivities = [];
  const postIssuanceSubjectivities = [];

  for (const subjectivity of availableSubjectivities) {
    if (subjectivity.subType === 'PreBind') {
      preBindSubjectivities.push(subjectivity);
    }

    if (subjectivity.subType === 'PreIssuance') {
      preIssuanceSubjectivities.push(subjectivity);
    }

    if (subjectivity.subType === 'PostIssuance') {
      postIssuanceSubjectivities.push(subjectivity);
    }
  }

  const sortedSubjectvites = [
    ...preBindSubjectivities.sort(sorter),
    ...preIssuanceSubjectivities.sort(sorter),
    ...postIssuanceSubjectivities.sort(sorter),
  ];

  function sorter(a: any, b: any) {
    return a.key.substring(1) - b.key.substring(1);
  }

  let defaults = {};
  sortedSubjectvites.map((subjectivity) => {
    defaults = {
      ...defaults,
      [`${subjectivity.id}_addToThisQuote`]: subjectivity.addToThisQuote,
      [`${subjectivity.id}_addToAllQuotes`]: subjectivity.addToAllQuotes,
      [`${subjectivity.id}_verified`]: subjectivity.verified,
      [`${subjectivity.id}_enforceMandatoryFlag`]:
        subjectivity.mandatory && subjectivity.addToThisQuote,
    };
    return defaults;
  });
  return { formattedData: defaults, sortedData: sortedSubjectvites };
};

// FIR-21154: UWs look for a subjectivity by its wording, not by its key, so the
// search matches on the description first. The ranked results are re-emitted in
// their original order because `formatSubjectivities` has already grouped the
// list by sub-type (Pre-Bind, then Pre-Issuance, then Post-Issuance) and letting
// match-sorter re-rank would break that grouping mid-typing.
export const filterSubjectivitiesBySearch = (
  subjectivities: any,
  searchTerm: string
) => {
  if (!_.isArray(subjectivities)) {
    return [];
  }

  const term = (searchTerm ?? '').trim();

  if (!term) {
    return subjectivities;
  }

  return matchSorter(subjectivities, term, {
    keys: ['description', 'subType', 'key'],
    sorter: (rankedItems) => rankedItems,
  });
};

export const getAddedSubjectivities = (subjectivites: any) => {
  const addedSubjectivities = subjectivites.filter((subjectivity: any) => {
    return subjectivity.addToThisQuote;
  });
  return addedSubjectivities.length;
};

export const getPolicyEffectiveDateRestrictions = (
  productType: any,
  isCowbeller: any
) => {
  let minDate = Moment().subtract(7, 'days').format('YYYY-MM-DD');
  let maxDate = Moment().add(60, 'days').format('YYYY-MM-DD');

  if (productType === ProductTypes.p100) {
    const minDaysInPast = isCowbeller ? 365 : 7;
    minDate = Moment().subtract(minDaysInPast, 'days').format('YYYY-MM-DD');
    maxDate = Moment().add(60, 'days').format('YYYY-MM-DD');
  }

  if (productType == ProductTypes.zsp) {
    minDate = Moment().subtract(5, 'days').format('YYYY-MM-DD');
    maxDate = Moment().add(90, 'days').format('YYYY-MM-DD');
  }

  return { minDate, maxDate };
};

export const getP250FormattedData = (data: any) => {
  return {
    customerFirstName: data?.customerFirstName ?? '',
    customerLastName: data?.customerLastName ?? '',
    customerEmail: data?.customerEmail ?? '',
    customerPhone: data?.customerPhone ?? '',
    agencyMailingAddress1: data?.firmographicData?.agencyMailingAddress1 ?? '',
    agencyMailingAddress2: data?.firmographicData?.agencyMailingAddress2 ?? '',
    agencyMailingCity: data?.firmographicData?.agencyMailingCity ?? '',
    agencyMailingState: data?.firmographicData?.agencyMailingState ?? '',
    agencyMailingZipCode: data?.firmographicData?.agencyMailingZipCode ?? '',
    surplusLicenseNumber: data?.firmographicData?.surplusLinesNumber ?? '',
    surplusLinesBrokerEmail:
      data?.firmographicData?.surplusLinesBrokerEmail ?? '',
    surplusLinesBrokerFirstName:
      data?.firmographicData?.surplusLinesBrokerFirstName ?? '',
    surplusLinesBrokerLastName:
      data?.firmographicData?.surplusLinesBrokerLastName ?? '',
    surplusLinesState: data?.firmographicData?.surplusLinesState
      ? data?.firmographicData?.surplusLinesState
      : data?.firmographicData?.state ?? '',
    effectiveDateOfPolicy: data?.initial250RequestData?.effectiveDate
      ? Moment.utc(data.initial250RequestData.effectiveDate).format(
          'YYYY-MM-DD'
        )
      : '-',
    premium: data?.totalPremium ? moneyFormat2Decimals(data.totalPremium) : '-',
    quoteNumber: data?.quoteNumber ?? '',
    companyName: data?.companyName ?? '',
    isSurplus: Boolean(data?.isSurplus),
    productType: data?.initial250RequestData?.product,
    accountId: data?.accountId,
    agencyId: data?.agencyId,
    policyNumber: data?.policyNumber,
    quoteId: data?.id,
    created: data?.created,
    boundOn: data?.boundOn,
    bindOrderDocumentInfo: data.bindOrderDocumentInfo,
    primaryIndustry:
      data?.firmographicData?.ui_primaryIndustryDescription ?? '',
    secondaryIndustry:
      data?.firmographicData?.ui_secondaryIndustryDescription || '',
    industryCode: data?.firmographicData?.industryCode ?? '',
    submissionId: data?.submissionId,
    isEndorsement: data?.isEndorsement ?? false,
  };
};

export const getP100FormattedData = (data: any) => {
  const deductible =
    data?.initialRequestData?.deductible != null
      ? moneyFormat2Decimals(data.initialRequestData.deductible)
      : '-';
  return {
    customerFirstName: data?.customerFirstName ?? '',
    customerLastName: data?.customerLastName ?? '',
    customerEmail: data?.customerEmail ?? '',
    customerPhone: data?.customerPhone ?? '',
    agencyMailingAddress1: data?.firmographicData?.agencyMailingAddress1 ?? '',
    agencyMailingAddress2: data?.firmographicData?.agencyMailingAddress2 ?? '',
    agencyMailingCity: data?.firmographicData?.agencyMailingCity ?? '',
    agencyMailingState: data?.firmographicData?.agencyMailingState ?? '',
    agencyMailingZipCode: data?.firmographicData?.agencyMailingZipCode ?? '',
    surplusLicenseNumber: data?.firmographicData?.surplusLinesNumber ?? '',
    surplusLinesBrokerEmail:
      data?.firmographicData?.surplusLinesBrokerEmail ?? '',
    surplusLinesBrokerFirstName:
      data?.firmographicData?.surplusLinesBrokerFirstName ?? '',
    surplusLinesBrokerLastName:
      data?.firmographicData?.surplusLinesBrokerLastName ?? '',
    surplusLinesState: data?.firmographicData?.surplusLinesState
      ? data?.firmographicData?.surplusLinesState
      : data?.firmographicData?.state ?? '',
    effectiveDateOfPolicy: data?.initialRequestData?.effectiveDate
      ? Moment.utc(data.initialRequestData.effectiveDate).format('YYYY-MM-DD')
      : '-',
    quoteNumber: data?.quoteNumber ?? '',
    companyName: data?.companyName ?? '',
    isSurplus: Boolean(data?.isSurplus),
    bindQuoteEffectiveDate: toUniversalUtcDate(
      data?.initialRequestData?.effectiveDate
    ),
    // Deprecated pre-formatted strings, kept for P100ConfirmIssuePolicy.tsx (outside this
    // slice's scope). New consumers should read the *Amount fields + currency below and
    // render with <CurrencyAmount> instead.
    deductible,
    limit: data?.initialRequestData?.limit
      ? formatCurrencyToTenthsPlaceWithUnit(data?.initialRequestData?.limit)
      : '-',
    premium: data?.totalPremium ? moneyFormat2Decimals(data.totalPremium) : '-',
    productType: data?.initialRequestData?.product,
    // ISO 4217 code from the API, plus raw amounts for <CurrencyAmount>. Never inferred.
    currency: data?.currency,
    deductibleAmount: data?.initialRequestData?.deductible,
    limitAmount: data?.initialRequestData?.limit,
    premiumAmount: data?.totalPremium,
    totalRevenueAmount: data?.firmographicData?.totalRevenue,
    numOfEmployees: data?.firmographicData?.noOfEmployeesAll ?? '-',
    naics: data?.firmographicData?.naicsCode ?? '-',
    ownershipType: data?.firmographicData?.ownershipType ?? '-',
    accountId: data?.accountId,
    agencyId: data?.agencyId,
    quoteId: data?.id,
    bindOrderDocumentInfo: data?.bindOrderDocumentInfo,
    primaryIndustry:
      data?.firmographicData?.ui_primaryIndustryDescription ?? '',
    secondaryIndustry:
      data?.firmographicData?.ui_secondaryIndustryDescription || '',
    industryCode: data?.firmographicData?.industryCode ?? '',
    submissionId:
      data?.submissionId ?? data?.calculatorGeneratedData?.submissionId,
  };
};

// ModalDetails (shared/ModalBase.tsx) renders a plain string, not <CurrencyAmount>, and
// is outside this slice's scope — see the `premium` field below. Formats with the API's
// own currency rather than a product/region guess; degrades to '-' on a missing amount or
// malformed currency, matching <CurrencyAmount>'s own placeholder behavior.
const formatMoneyForLegacyModal = (
  amount: number | undefined,
  currency: string | undefined
) => {
  if (amount == null || Number.isNaN(amount)) {
    return '-';
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return '-';
  }
};

export const getFormattedQuoteData = (data: any) => {
  return {
    customerFirstName: data?.quote?.customerFirstName ?? '',
    customerLastName: data?.quote?.customerLastName ?? '',
    customerEmail: data?.quote?.customerEmail ?? '',
    customerPhone: data?.quote?.customerPhone ?? '',
    companyName: data?.quote?.companyName ?? '',
    agencyMailingAddress1: data?.firmographicData?.agencyMailingAddress1 ?? '',
    agencyMailingAddress2: data?.firmographicData?.agencyMailingAddress2 ?? '',
    agencyMailingCity: data?.firmographicData?.agencyMailingCity ?? '',
    agencyMailingState: data?.firmographicData?.agencyMailingState ?? '',
    agencyMailingZipCode: data?.firmographicData?.agencyMailingZipCode ?? '',
    surplusLicenseNumber: data?.firmographicData?.surplusLinesNumber ?? '',
    surplusLinesBrokerEmail:
      data?.firmographicData?.surplusLinesBrokerEmail ?? '',
    surplusLinesBrokerFirstName:
      data?.firmographicData?.surplusLinesBrokerFirstName ?? '',
    surplusLinesBrokerLastName:
      data?.firmographicData?.surplusLinesBrokerLastName ?? '',
    surplusLinesState: data?.firmographicData?.surplusLinesState
      ? data?.firmographicData?.surplusLinesState
      : data?.firmographicData?.state ?? '',
    effectiveDateOfPolicy: data?.quote?.effectiveDate
      ? Moment.utc(data?.quote?.effectiveDate).format('YYYY-MM-DD')
      : '-',
    quoteNumber: data?.quote?.quoteNumber ?? '',
    isSurplus: Boolean(data?.quote?.isSurplus),
    productType: data?.firmographicData?.product,
    accountId: data?.firmographicData?.accountId,
    agencyId: data?.quote?.agencyId,
    policyNumber: data?.quote?.policyNumber,
    quoteId: data?.quote?.id,
    created: data?.firmographicData?.created,
    boundOn: data?.quote?.boundOn,
    // ISO 4217 code from the API, plus raw amounts for <CurrencyAmount>. Never inferred
    // from product/region — a missing currency here is a backend bug to report. Read from
    // either the GetQuoteDetailsResult wrapper or the nested quote: MS-268 documents the
    // field as living on the wrapper, but this same response is also unwrapped as
    // `payload.quote.currency` elsewhere (quotes.services.ts formatPrimeXQuote), so check
    // both rather than assume one and silently drop a currency the API did send.
    currency: data?.currency ?? data?.quote?.currency,
    // Deprecated pre-formatted string, kept for ModalDetails/ConfirmIssuePolicy.tsx
    // (apps/console/src/components/modals/ConfirmIssuePolicy.tsx via
    // bind-quote/stepper-screens/shared/ModalBase.tsx — outside this slice's scope, and
    // shared with P250's issue-policy modal). New consumers should read premiumAmount +
    // currency below and render with <CurrencyAmount> instead.
    premium: formatMoneyForLegacyModal(
      data?.quote?.totalPremium,
      data?.currency ?? data?.quote?.currency
    ),
    premiumAmount: data?.quote?.totalPremium,
    deductibleAmount: data?.quote?.deductible,
    // Excess towers carry their layers in globalValue; the Cowbell layer's
    // attachment is the sum of the limits beneath it (shown in place of a
    // retention for excess quotes).
    attachmentAmount: deriveExcessAttachment(
      data?.globalValue as TowerValue | undefined
    ),
    limitAmount: data?.quote?.aggLimit,
    bindQuoteEffectiveDate: toUniversalUtcDate(data?.quote?.effectiveDate),
    totalRevenueAmount: data?.firmographicData?.totalRevenue,
    numOfEmployees: data?.firmographicData?.noOfEmployeesAll ?? '-',
    naics: data?.firmographicData?.naicsCode ?? '-',
    ownershipType: data?.firmographicData?.ownershipType ?? '-',
    primaryIndustry:
      data?.firmographicData?.ui_primaryIndustryDescription ?? '',
    secondaryIndustry:
      data?.firmographicData?.ui_secondaryIndustryDescription || '',
    industryCode: data?.firmographicData?.industryCode ?? '',
    bindOrderDocumentInfo: data?.bindOrderDocumentInfo,
    submissionId: data?.quote?.submissionId,
    isEndorsement: data?.quote?.isEndorsement ?? false,
  };
};

export const handleError = ({
  error,
  notifications,
  fallbackMessage = 'Unable to complete step at this time. Please try again later.',
}: any) => {
  if (
    error?.response?.status === 412 &&
    Array.isArray(error?.response?.data?.errors)
  ) {
    return notifications.show({
      type: 'error',
      title: 'Unable to complete step.',
      position: 'top-center',
      persist: true,
      content: (key: any, message: any) => {
        return (
          <PaperSnackbar key={key} message={message}>
            <strong>
              The step could not be completed for the following reason(s):
            </strong>
            <ul>
              {error.response.data.errors.map((error: any) => (
                <li key={error.detail}>{error.detail}</li>
              ))}
            </ul>
          </PaperSnackbar>
        );
      },
    });
  }

  if (
    Array.isArray(error?.response?.data) &&
    error?.response?.data?.length > 0
  ) {
    return notifications.show({
      type: 'error',
      title: 'Unable to complete step',
      position: 'top-center',
      persist: true,
      content: (key: any, message: any) => {
        return (
          <PaperSnackbar key={key} message={message}>
            <strong>
              The step could not be completed for the following reason(s):
            </strong>
            <ul>
              {error.response.data.map((error: any) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </PaperSnackbar>
        );
      },
    });
  }

  notifications.error(fallbackMessage);
};

export const sendEmailText = (quote: any) => {
  const { customerFirstName, customerLastName, companyName } = quote;
  return (
    <>
      <p>
        Are you sure you want to invite{' '}
        <Span className={REDACTED_FIELD}>{customerFirstName}</Span>{' '}
        <Span className={REDACTED_FIELD}>{customerLastName}</Span>,{' '}
        <Span className={REDACTED_FIELD}>{companyName}</Span> to attest their
        application?
      </p>
      <p>
        Once the application has been signed by the insured, we will issue the
        policy. <br /> You will receive a notification via email with a
        confirmation.
      </p>
      <p>
        <i>
          NOTE: If the insured needs to make any changes to the application
          details, <br /> the original binder will be invalid due to material
          changes. The account will need to be requoted.
        </i>
      </p>
    </>
  );
};

const Span = styled('span')({
  fontWeight: 600,
});

export const SubjectivitySubType = {
  PreBind: 'PreBind',
  PreIssuance: 'PreIssuance',
  PostIssuance: 'PostIssuance',
};

export const SubjectivitySubTypeLabelMap = {
  [SubjectivitySubType.PreBind]: 'Pre-Bind',
  [SubjectivitySubType.PreIssuance]: 'Pre-Issuance',
  [SubjectivitySubType.PostIssuance]: 'Post-Issuance',
};

export const determineIfStepIsEditable = ({
  subjectivitySubType,
  status,
  isAPI,
  productType,
}: any) => isStepEditable({ subjectivitySubType, status, isAPI, productType });

export const determineIsUKProduct = (product: any) => {
  return product === ProductTypes.prime_one;
};

export const determineAUProduct = (product: any) => {
  return product === ProductTypes.prime_one_au;
};
